import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Box, Button, Checkbox, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Select, Spinner, Stack, Table, Tbody, Td, Text, Th,
  Thead, Tooltip, Tr, useBreakpointValue, useToast,
} from '@chakra-ui/react';
import {
  useStripeSubscriptionsPreview, useImportStripeSubscriptions, StripeSubscriptionPreview,
} from '../hooks/useRecurringRevenues';
import useProjects from '../hooks/useProjects';
import useContacts from '../hooks/useContacts';
import { frequencyLabel } from '../models/recurringRevenue';
import { money } from '../utils/money';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// item 1125 (Chad: "multiple for this company with this price") — same-customer/same-amount
// subs are indistinguishable without the Stripe line detail. First product line shows inline
// under the email; the full picture (start date, description, every line) rides the hover.
const subDetailTip = (r: StripeSubscriptionPreview): React.ReactNode => (
  <Box fontSize="xs">
    {r.created && <Text fontWeight="bold">{`Started ${r.created.slice(0, 10)}`}</Text>}
    {r.description && <Text>{r.description}</Text>}
    {(r.lines ?? []).map((l) => <Text key={l}>{l}</Text>)}
    <Text opacity={0.6} mt={1}>{r.stripeSubscriptionId}</Text>
  </Box>
);

const subCustomerCell = (r: StripeSubscriptionPreview): React.ReactNode => (
  <Tooltip label={subDetailTip(r)} placement="right" hasArrow>
    <Box cursor="help">
      <Text fontSize="sm">{r.customerName || '—'}</Text>
      <Text fontSize="xs" color="gray.500">{r.customerEmail || r.stripeCustomerId}</Text>
      {(r.lines?.length ?? 0) > 0 && (
        <Text fontSize="xs" color="purple.300" noOfLines={1} maxW="260px">
          {(r.lines ?? []).join(' · ')}
        </Text>
      )}
    </Box>
  </Tooltip>
);

interface RowChoice {
  include: boolean;
  customerId: string;
  projectId: string;
  name: string;
}

// item 731g / 730 (Chad) — ADOPT existing live Stripe subscriptions. A read-only dry-run lists
// every Stripe subscription, matched to a CRM customer by email; you assign a project to each and
// import — Basix links the EXISTING sub_ id (no cancel/re-enroll, no client re-auth). Already-linked
// subs show as such and can't be re-imported.
const ImportStripeSubscriptionsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { data, status, error } = useStripeSubscriptionsPreview(isOpen);
  const { data: projects } = useProjects();
  const { data: contacts } = useContacts();
  const importer = useImportStripeSubscriptions();
  const toast = useToast();
  // item 1508 (Chad, iPhone screenshots) — a 6xl modal with a wide table is unusable on a phone;
  // full-screen below md. Chakra 1.6.7's Modal takes no responsive size object, hence the hook.
  const modalSize = useBreakpointValue({ base: 'full', md: '6xl' }) ?? '6xl';

  const [choices, setChoices] = useState<Record<string, RowChoice>>({});

  // item 1129 (Chad) — canceled Stripe subs are history, not import candidates: hide them
  // (an already-linked canceled one still shows as Linked so the record isn't mysterious)
  const rows = useMemo(() => (data?.items ?? []).filter(
    (r) => r.alreadyImported || (r.status !== 'canceled' && r.status !== 'incomplete_expired'),
  ), [data]);
  const customers = useMemo(
    () => (contacts?.index ?? []).filter((c) => !c.isVendor),
    [contacts],
  );

  // seed each importable row from its email match once the preview loads
  useEffect(() => {
    if (!rows.length) return;
    setChoices((prev) => {
      const next = { ...prev };
      rows.forEach((r) => {
        if (r.alreadyImported || next[r.stripeSubscriptionId]) return;
        next[r.stripeSubscriptionId] = {
          include: false,
          customerId: r.matchedCustomerId ?? '',
          projectId: '',
          name: r.customerName ? `${r.customerName} — Subscription` : 'Imported subscription',
        };
      });
      return next;
    });
  }, [rows]);

  const setChoice = (id: string, patch: Partial<RowChoice>) => setChoices(
    (prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }),
  );

  const selected = rows.filter((r) => !r.alreadyImported && choices[r.stripeSubscriptionId]?.include);
  const ready = selected.filter((r) => {
    const c = choices[r.stripeSubscriptionId];
    return c?.customerId && c?.projectId;
  });
  const canImport = selected.length > 0 && ready.length === selected.length;

  const doImport = async () => {
    const payload = ready.map((r) => {
      const c = choices[r.stripeSubscriptionId];
      return {
        stripeSubscriptionId: r.stripeSubscriptionId,
        customerId: c.customerId,
        projectId: c.projectId,
        name: c.name?.trim() || 'Imported subscription',
      };
    });
    try {
      const res = await importer.mutateAsync(payload);
      toast({
        status: 'success',
        title: `Imported ${res.imported} subscription${res.imported === 1 ? '' : 's'}`,
        description: res.skipped?.length ? `${res.skipped.length} skipped (already linked or invalid).` : undefined,
      });
      onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Import failed', description: String(e?.response?.data ?? 'Could not import.') });
    }
  };

  const projectOptions = projects?.index ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={modalSize} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Import subscriptions from Stripe</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            This adopts your existing live Stripe subscriptions — Basix links the current Stripe
            subscription (no cancel, no re-enroll, no client re-authorization). Assign each one a
            customer and project, then import. Nothing is charged and no price changes.
          </Text>

          {status === 'loading' && (
            <HStack py={8} justify="center">
              <Spinner />
              <Text color="gray.500">Reading Stripe…</Text>
            </HStack>
          )}
          {status === 'error' && (
            <Text color="red.400">{String((error as any)?.response?.data ?? 'Could not read Stripe subscriptions.')}</Text>
          )}
          {status === 'success' && data && !data.available && (
            <Text color="gray.500">Stripe isn’t configured, so there’s nothing to import.</Text>
          )}
          {status === 'success' && data?.available && rows.length === 0 && (
            <Text color="gray.500">No subscriptions found in Stripe.</Text>
          )}

          {status === 'success' && rows.length > 0 && (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th />
                    <Th>Stripe customer</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Cadence</Th>
                    <Th>Status</Th>
                    <Th>Basix customer</Th>
                    <Th>Project</Th>
                    <Th>Name</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((r: StripeSubscriptionPreview) => {
                    const c = choices[r.stripeSubscriptionId];
                    if (r.alreadyImported) {
                      return (
                        <Tr key={r.stripeSubscriptionId} opacity={0.5}>
                          <Td><Badge colorScheme="green">Linked</Badge></Td>
                          <Td>{subCustomerCell(r)}</Td>
                          <Td isNumeric>{money(r.amount)}</Td>
                          <Td>{frequencyLabel(r.frequency, r.customIntervalDays ?? undefined)}</Td>
                          <Td><Text fontSize="xs">{r.status}</Text></Td>
                          <Td colSpan={3}><Text fontSize="xs" color="gray.500">Already in Basix</Text></Td>
                        </Tr>
                      );
                    }
                    return (
                      <Tr key={r.stripeSubscriptionId}>
                        <Td>
                          <Checkbox
                            isChecked={c?.include ?? false}
                            onChange={(e) => setChoice(r.stripeSubscriptionId, { include: e.target.checked })}
                          />
                        </Td>
                        <Td>{subCustomerCell(r)}</Td>
                        <Td isNumeric>{money(r.amount)}</Td>
                        <Td>{frequencyLabel(r.frequency, r.customIntervalDays ?? undefined)}</Td>
                        <Td>
                          <Text fontSize="xs">{r.status}</Text>
                          {r.autoPay && <Badge colorScheme="blue" fontSize="0.6rem">auto-pay</Badge>}
                        </Td>
                        <Td>
                          <Select
                            size="sm"
                            minW="160px"
                            placeholder="— Customer —"
                            value={c?.customerId ?? ''}
                            // item 1129 — switching customers clears a project that no longer belongs
                            onChange={(e) => setChoice(r.stripeSubscriptionId, { customerId: e.target.value, projectId: '' })}
                          >
                            {customers.map((cust) => (<option key={cust.id} value={cust.id}>{cust.name}</option>))}
                          </Select>
                        </Td>
                        <Td>
                          {/* item 1129 (Chad) — only the CHOSEN customer's projects; picking a
                              different customer clears a project that no longer belongs */}
                          <Select
                            size="sm"
                            minW="160px"
                            placeholder={c?.customerId ? '— Project —' : '— Customer first —'}
                            isDisabled={!c?.customerId}
                            value={c?.projectId ?? ''}
                            onChange={(e) => setChoice(r.stripeSubscriptionId, { projectId: e.target.value })}
                          >
                            {projectOptions
                              .filter((p) => p.customerId === c?.customerId)
                              .map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                          </Select>
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            minW="160px"
                            value={c?.name ?? ''}
                            onChange={(e) => setChoice(r.stripeSubscriptionId, { name: e.target.value })}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Stack direction="row" align="center" w="100%">
            <Text fontSize="sm" color="gray.500">
              {selected.length > 0
                ? `${selected.length} selected${ready.length < selected.length ? ` · assign a customer + project to all` : ''}`
                : 'Select subscriptions to import'}
            </Text>
            <Box flex="1" />
            <Button variant="ghost" onClick={onClose} isDisabled={importer.isLoading}>Cancel</Button>
            <Button
              colorScheme="brandGreen"
              onClick={doImport}
              isDisabled={!canImport}
              isLoading={importer.isLoading}
              loadingText="Importing"
            >
              {`Import ${selected.length || ''}`.trim()}
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportStripeSubscriptionsModal;
